import { useCallback, useEffect, useRef, useState } from "react";
import {
  dataTransferHasFiles,
  parseDataTransferItems,
  PermissionSeed,
} from "@/src/components/data_room/file_uploaders/parseDataTransferFiles";
import {
  fileWrapper,
  folderWrapper,
} from "@/src/components/data_room/file_uploaders/NewFilesUploader";

export type ExternalDropTarget = {
  folderId: string;
  folderName: string;
  permission: PermissionSeed | null;
};

type UseExternalFileDropArgs = {
  enabled: boolean;
  /** Drop on the empty/surface area (current folder) */
  surfaceTarget: ExternalDropTarget | null;
  onDrop: (args: {
    files: fileWrapper[];
    folders: folderWrapper[];
    target: ExternalDropTarget;
  }) => void;
};

/**
 * Native OS file/folder drag-and-drop for the data room surface.
 * Ignores Atlaskit internal moves (no Files type in dataTransfer).
 */
export function useExternalFileDrop(args: UseExternalFileDropArgs) {
  const { enabled, surfaceTarget, onDrop } = args;
  const [isSurfaceDragOver, setIsSurfaceDragOver] = useState(false);
  const [hoveredFolderId, setHoveredFolderId] = useState<string | null>(null);
  const dragDepthRef = useRef(0);
  const folderTargetsRef = useRef<Map<string, ExternalDropTarget>>(new Map());

  const registerFolderTarget = useCallback(
    (target: ExternalDropTarget, element: HTMLElement | null) => {
      if (!element) {
        folderTargetsRef.current.delete(target.folderId);
        return;
      }
      folderTargetsRef.current.set(target.folderId, target);
      element.setAttribute("data-external-drop-folder-id", target.folderId);
    },
    [],
  );

  const handleSurfaceDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (!enabled || !dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current += 1;
      setIsSurfaceDragOver(true);
    },
    [enabled],
  );

  const handleSurfaceDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (!enabled || !dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) {
        setIsSurfaceDragOver(false);
        setHoveredFolderId(null);
        clearExternalFolderHighlights();
      }
    },
    [enabled],
  );

  const handleSurfaceDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!enabled || !dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";

      const el = (e.target as HTMLElement | null)?.closest?.(
        "[data-external-drop-folder-id]",
      ) as HTMLElement | null;
      const folderId = el?.getAttribute("data-external-drop-folder-id") ?? null;

      if (folderId !== hoveredFolderId) {
        setHoveredFolderId(folderId);
        clearExternalFolderHighlights();
        if (folderId && el) {
          el.setAttribute("data-external-drop-over", "true");
        }
      }
    },
    [enabled, hoveredFolderId],
  );

  const handleSurfaceDrop = useCallback(
    async (e: React.DragEvent) => {
      if (!enabled || !dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();

      dragDepthRef.current = 0;
      setIsSurfaceDragOver(false);
      clearExternalFolderHighlights();

      const folderId =
        (
          e.target as HTMLElement | null
        )?.closest?.("[data-external-drop-folder-id]")?.getAttribute(
          "data-external-drop-folder-id",
        ) ?? hoveredFolderId;

      const target =
        (folderId ? folderTargetsRef.current.get(folderId) : null) ??
        surfaceTarget;

      setHoveredFolderId(null);

      if (!target) return;

      const { files, folders } = await parseDataTransferItems(
        e.dataTransfer.items,
        target.permission,
      );

      if (files.length === 0 && folders.length === 0) return;

      onDrop({ files, folders, target });
    },
    [enabled, hoveredFolderId, onDrop, surfaceTarget],
  );

  useEffect(() => {
    if (!enabled) {
      dragDepthRef.current = 0;
      setIsSurfaceDragOver(false);
      setHoveredFolderId(null);
      clearExternalFolderHighlights();
    }
  }, [enabled]);

  return {
    isSurfaceDragOver,
    hoveredFolderId,
    registerFolderTarget,
    surfaceHandlers: {
      onDragEnter: handleSurfaceDragEnter,
      onDragLeave: handleSurfaceDragLeave,
      onDragOver: handleSurfaceDragOver,
      onDrop: handleSurfaceDrop,
    },
  };
}

function clearExternalFolderHighlights() {
  document
    .querySelectorAll("[data-external-drop-over='true']")
    .forEach((node) => {
      node.removeAttribute("data-external-drop-over");
    });
}
